import React, { useEffect, useState, useCallback } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  arrayMove, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Eye, EyeOff, ChevronRight, ChevronDown, ChevronLeft as ChevronLeftIcon,
  Pencil, Save, X, Plus, Trash2, Loader2, Lock, AlertCircle, CheckCircle2, Menu as MenuIcon,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

/**
 * AdminMenusPage — fully visual menu tree editor.
 *
 * Capabilities:
 *  • Drag-and-drop to reorder items within the same parent.
 *  • Indent button → makes the item a child of its preceding sibling
 *    (which becomes a dropdown if it wasn't already).
 *  • Outdent button → promotes the item to its grandparent's level.
 *  • Edit label_es / label_en inline.
 *  • Toggle visibility (eye icon). Protected items can't be deleted but
 *    can still be hidden, reordered, indented, etc.
 *  • Add new top-level items or new children. Custom items can be deleted.
 *
 * The page operates on a local copy of the tree and submits the whole shape
 * to PUT /api/admin/menus/tree atomically when the user clicks Save.
 */
export default function AdminMenusPage() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [addingToParent, setAddingToParent] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/menus/tree');
      setTree(data || []);
      // Expand all top-level dropdowns by default.
      const ids = new Set();
      (data || []).forEach((n) => { if (n.is_dropdown) ids.add(n.id); });
      setExpanded(ids);
      setDirty(false);
    } catch (err) {
      setFeedback({ type: 'error', message: formatApiError(err.response?.data?.detail) });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchTree(); }, [fetchTree]);

  useEffect(() => {
    if (feedback) {
      const id = setTimeout(() => setFeedback(null), 3500);
      return () => clearTimeout(id);
    }
  }, [feedback]);

  // ----- Tree mutation helpers (operate on the local tree copy) -----
  function findParent(roots, targetId, parent = null) {
    for (const n of roots) {
      if (n.id === targetId) return { parent, siblings: roots, node: n };
      if (n.children) {
        const r = findParent(n.children, targetId, n);
        if (r) return r;
      }
    }
    return null;
  }

  function patchTree(mutator) {
    setTree((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));
      mutator(clone);
      return clone;
    });
    setDirty(true);
  }

  function reorderWithinParent(parentId, sourceId, overId) {
    patchTree((cloned) => {
      const list = parentId === null ? cloned
        : (findParent(cloned, parentId)?.node.children || []);
      if (!list) return;
      const from = list.findIndex((n) => n.id === sourceId);
      const to = list.findIndex((n) => n.id === overId);
      if (from < 0 || to < 0) return;
      const moved = arrayMove(list, from, to);
      if (parentId === null) {
        cloned.length = 0;
        moved.forEach((n) => cloned.push(n));
      } else {
        const p = findParent(cloned, parentId)?.node;
        if (p) p.children = moved;
      }
    });
  }

  function indent(node) {
    patchTree((cloned) => {
      const info = findParent(cloned, node.id);
      if (!info) return;
      const idx = info.siblings.findIndex((n) => n.id === node.id);
      if (idx <= 0) return; // No preceding sibling to nest under.
      const prevSibling = info.siblings[idx - 1];
      // Remove the node from current parent and append as last child of prev sibling.
      info.siblings.splice(idx, 1);
      prevSibling.children = prevSibling.children || [];
      prevSibling.children.push(node);
      prevSibling.is_dropdown = true; // Becomes a container.
      setExpanded((prev) => new Set(prev).add(prevSibling.id));
    });
  }

  function outdent(node) {
    patchTree((cloned) => {
      const info = findParent(cloned, node.id);
      if (!info || !info.parent) return; // Already at root.
      const grandParentInfo = findParent(cloned, info.parent.id);
      if (!grandParentInfo) return;
      // Remove from parent's children.
      const idxInParent = info.parent.children.findIndex((n) => n.id === node.id);
      info.parent.children.splice(idxInParent, 1);
      // Insert into grandparent's siblings right AFTER the parent.
      const idxOfParent = grandParentInfo.siblings.findIndex((n) => n.id === info.parent.id);
      grandParentInfo.siblings.splice(idxOfParent + 1, 0, node);
      // If parent ended up with no children, demote dropdown flag.
      if (info.parent.children.length === 0) {
        info.parent.is_dropdown = false;
      }
    });
  }

  function toggleVisibility(node) {
    patchTree((cloned) => {
      const info = findParent(cloned, node.id);
      if (info) info.node.visible = !info.node.visible;
    });
  }

  function updateLabels(nodeId, { label_es, label_en, path, path_kind, icon }) {
    patchTree((cloned) => {
      const info = findParent(cloned, nodeId);
      if (info) {
        if (typeof label_es === 'string') info.node.label_es = label_es;
        if (typeof label_en === 'string') info.node.label_en = label_en;
        if (typeof path === 'string') info.node.path = path;
        if (typeof path_kind === 'string') info.node.path_kind = path_kind;
        if (typeof icon === 'string') info.node.icon = icon;
      }
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const minify = (nodes) => nodes.map((n) => ({
        id: n.id, parent_id: n.parent_id || null,
        children: minify(n.children || []),
      }));
      await api.put('/admin/menus/tree', { tree: minify(tree) });
      // Also save label/visibility changes (PATCH each node individually — cheap).
      const flat = [];
      const walk = (nodes) => nodes.forEach((n) => { flat.push(n); walk(n.children || []); });
      walk(tree);
      for (const n of flat) {
        await api.patch(`/admin/menus/${n.id}`, {
          label_es: n.label_es,
          label_en: n.label_en,
          visible: n.visible,
          path: n.path,
          path_kind: n.path_kind,
          icon: n.icon || '',
        });
      }
      setFeedback({ type: 'success', message: 'Menú guardado correctamente.' });
      await fetchTree();
    } catch (err) {
      setFeedback({ type: 'error', message: formatApiError(err.response?.data?.detail) || 'Error al guardar' });
    } finally { setSaving(false); }
  }

  async function handleAdd(parentId, payload) {
    try {
      const { data } = await api.post('/admin/menus', { ...payload, parent_id: parentId });
      // Insert into local tree.
      patchTree((cloned) => {
        const node = { ...data, children: [] };
        if (parentId === null) cloned.push(node);
        else {
          const info = findParent(cloned, parentId);
          if (info) {
            info.node.children = info.node.children || [];
            info.node.children.push(node);
          }
        }
      });
      setAddingToParent(null);
      setFeedback({ type: 'success', message: 'Menú añadido.' });
    } catch (err) {
      setFeedback({ type: 'error', message: formatApiError(err.response?.data?.detail) || 'Error' });
    }
  }

  async function handleDelete(node) {
    if (!window.confirm(`¿Eliminar "${node.label_es}" y sus sub-elementos?`)) return;
    try {
      await api.delete(`/admin/menus/${node.id}`);
      patchTree((cloned) => {
        const info = findParent(cloned, node.id);
        if (!info) return;
        const idx = info.siblings.findIndex((n) => n.id === node.id);
        if (idx >= 0) info.siblings.splice(idx, 1);
      });
      setFeedback({ type: 'success', message: 'Menú eliminado.' });
    } catch (err) {
      setFeedback({ type: 'error', message: formatApiError(err.response?.data?.detail) || 'Error' });
    }
  }

  function handleDragEnd(parentId, event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    reorderWithinParent(parentId, active.id, over.id);
  }

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>;
  }

  return (
    <div data-testid="admin-menus-page" className="max-w-4xl">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <MenuIcon size={22} />Menús del sitio
          </h1>
          <p className="text-charcoal-500 text-sm mt-1 max-w-xl">
            Arrastra los elementos para reordenarlos. Usa <Lock size={11} className="inline align-text-bottom" /> para identificar menús del sistema (no se pueden eliminar pero sí editar/ocultar). Los cambios se aplican al pulsar &laquo;Guardar&raquo;.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setAddingToParent(null)}
            data-testid="menus-add-root-btn"
            className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-charcoal-700 hover:bg-cmic-50 rounded-lg px-3 py-2 text-sm font-medium"
          >
            <Plus size={14} />Añadir menú raíz
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            data-testid="menus-save-btn"
            className={`inline-flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold ${
              dirty ? 'bg-cmic-600 text-white hover:bg-cmic-700' : 'bg-charcoal-100 text-charcoal-400'
            } disabled:opacity-50`}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Guardando…' : (dirty ? 'Guardar cambios' : 'Sin cambios')}
          </button>
        </div>
      </div>

      {feedback && (
        <div
          data-testid="menus-feedback"
          className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-5 text-sm font-medium transition-all ${
            feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {feedback.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {feedback.message}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-2 sm:p-3">
        <SortableLevel
          parentId={null}
          items={tree}
          sensors={sensors}
          onDragEnd={(e) => handleDragEnd(null, e)}
          renderItem={(node) => (
            <MenuNode
              key={node.id}
              node={node}
              depth={0}
              expanded={expanded}
              setExpanded={setExpanded}
              editingId={editingId}
              setEditingId={setEditingId}
              addingToParent={addingToParent}
              setAddingToParent={setAddingToParent}
              indent={indent}
              outdent={outdent}
              toggleVisibility={toggleVisibility}
              updateLabels={updateLabels}
              handleDelete={handleDelete}
              handleAdd={handleAdd}
              handleDragEnd={handleDragEnd}
              sensors={sensors}
            />
          )}
        />
        {addingToParent === null && <NewMenuRow onCancel={() => setAddingToParent('NONE')} onSubmit={(payload) => handleAdd(null, payload)} />}
      </div>
    </div>
  );
}

function SortableLevel({ parentId, items, sensors, onDragEnd, renderItem }) {
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((n) => n.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {items.map(renderItem)}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function MenuNode({ node, depth, expanded, setExpanded, editingId, setEditingId, addingToParent, setAddingToParent, indent, outdent, toggleVisibility, updateLabels, handleDelete, handleAdd, handleDragEnd, sensors }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: node.id });
  const isOpen = expanded.has(node.id);
  const hasChildren = (node.children || []).length > 0;
  const isEditing = editingId === node.id;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  function toggleExpand() {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(node.id) ? next.delete(node.id) : next.add(node.id);
      return next;
    });
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        data-testid={`menu-row-${node.menu_key || node.id}`}
        className={`flex items-center gap-2 px-2 py-2 rounded-lg border ${node.visible ? 'bg-white border-gray-100' : 'bg-gray-50 border-gray-200 opacity-70'} hover:border-cmic-200`}
      >
        <button
          {...attributes}
          {...listeners}
          aria-label="Arrastrar para reordenar"
          data-testid={`menu-drag-${node.menu_key || node.id}`}
          className="cursor-grab active:cursor-grabbing text-charcoal-300 hover:text-charcoal-600 p-1"
        >
          <GripVertical size={16} />
        </button>

        {hasChildren ? (
          <button
            onClick={toggleExpand}
            aria-label={isOpen ? 'Colapsar' : 'Expandir'}
            className="text-charcoal-500 hover:text-cmic-600 p-1"
          >
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : (
          <span className="w-6" />
        )}

        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-2">
              <div className="flex gap-2 flex-wrap">
                <input
                  value={node.label_es}
                  onChange={(e) => updateLabels(node.id, { label_es: e.target.value })}
                  data-testid={`menu-label-es-input-${node.menu_key || node.id}`}
                  placeholder="Etiqueta ES"
                  className="flex-1 min-w-[140px] rounded-md border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                />
                <input
                  value={node.label_en}
                  onChange={(e) => updateLabels(node.id, { label_en: e.target.value })}
                  data-testid={`menu-label-en-input-${node.menu_key || node.id}`}
                  placeholder="Label EN"
                  className="flex-1 min-w-[140px] rounded-md border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                />
              </div>
              {!node.is_dropdown && (
                <div className="flex gap-2 flex-wrap">
                  <select
                    value={node.path_kind || 'route'}
                    onChange={(e) => updateLabels(node.id, { path_kind: e.target.value })}
                    data-testid={`menu-path-kind-${node.menu_key || node.id}`}
                    className="rounded-md border border-gray-200 px-2 py-1 text-xs"
                    disabled={node.protected}
                    title={node.protected ? 'Protected items cannot change kind' : ''}
                  >
                    <option value="route">Ruta interna</option>
                    <option value="bilingual">Slug bilingüe</option>
                    <option value="external_url">URL externa</option>
                  </select>
                  <input
                    value={node.path || ''}
                    onChange={(e) => updateLabels(node.id, { path: e.target.value })}
                    data-testid={`menu-path-${node.menu_key || node.id}`}
                    placeholder={
                      (node.path_kind || 'route') === 'external_url' ? 'https://...'
                      : (node.path_kind || 'route') === 'bilingual'  ? 'es-slug|en-slug'
                      : 'ruta-interna'
                    }
                    disabled={node.protected}
                    className="flex-1 min-w-[180px] rounded-md border border-gray-200 px-2 py-1 text-xs disabled:bg-gray-50"
                  />
                  <select
                    value={node.icon || ''}
                    onChange={(e) => updateLabels(node.id, { icon: e.target.value })}
                    data-testid={`menu-icon-${node.menu_key || node.id}`}
                    className="rounded-md border border-gray-200 px-2 py-1 text-xs"
                  >
                    {['', 'facebook', 'instagram', 'linkedin', 'twitter', 'youtube',
                      'globe', 'mail', 'phone', 'external-link', 'rss', 'github',
                      'map-pin', 'calendar'].map((ic) => (
                      <option key={ic || 'none'} value={ic}>{ic || '— sin icono —'}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium truncate ${node.visible ? 'text-charcoal-800' : 'text-charcoal-400'}`}>
                {node.label_es}
              </span>
              <span className="text-xs text-charcoal-400 truncate">/ {node.label_en}</span>
              {node.is_dropdown && (
                <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                  dropdown
                </span>
              )}
              {node.protected && (
                <span title="Menú del sistema (no eliminable)"><Lock size={11} className="text-charcoal-400" /></span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          {depth > 0 && (
            <button
              onClick={() => outdent(node)}
              data-testid={`menu-outdent-${node.menu_key || node.id}`}
              title="Promover (sacar del submenú)"
              className="p-1.5 text-charcoal-500 hover:text-cmic-600 hover:bg-cmic-50 rounded"
            >
              <ChevronLeftIcon size={14} />
            </button>
          )}
          {depth < 2 && (
            <button
              onClick={() => indent(node)}
              data-testid={`menu-indent-${node.menu_key || node.id}`}
              title="Sangrar (convertir en submenú del anterior)"
              className="p-1.5 text-charcoal-500 hover:text-cmic-600 hover:bg-cmic-50 rounded"
            >
              <ChevronRight size={14} />
            </button>
          )}
          <button
            onClick={() => toggleVisibility(node)}
            data-testid={`menu-visibility-${node.menu_key || node.id}`}
            title={node.visible ? 'Ocultar' : 'Mostrar'}
            className={`p-1.5 rounded ${node.visible ? 'text-emerald-600 hover:bg-emerald-50' : 'text-rose-500 hover:bg-rose-50'}`}
          >
            {node.visible ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          {isEditing ? (
            <button
              onClick={() => setEditingId(null)}
              data-testid={`menu-edit-done-${node.menu_key || node.id}`}
              className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
              title="Listo"
            >
              <CheckCircle2 size={14} />
            </button>
          ) : (
            <button
              onClick={() => setEditingId(node.id)}
              data-testid={`menu-edit-${node.menu_key || node.id}`}
              className="p-1.5 text-charcoal-500 hover:text-cmic-600 hover:bg-cmic-50 rounded"
              title="Editar etiquetas"
            >
              <Pencil size={13} />
            </button>
          )}
          {depth < 2 && (
            <button
              onClick={() => setAddingToParent(node.id)}
              data-testid={`menu-add-child-${node.menu_key || node.id}`}
              title="Añadir sub-elemento"
              className="p-1.5 text-charcoal-500 hover:text-cmic-600 hover:bg-cmic-50 rounded"
            >
              <Plus size={13} />
            </button>
          )}
          {!node.protected && (
            <button
              onClick={() => handleDelete(node)}
              data-testid={`menu-delete-${node.menu_key || node.id}`}
              title="Eliminar"
              className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {isOpen && hasChildren && (
        <div className="ml-8 pl-3 border-l-2 border-cmic-100 space-y-1 mt-1">
          <SortableLevel
            parentId={node.id}
            items={node.children}
            sensors={sensors}
            onDragEnd={(e) => handleDragEnd(node.id, e)}
            renderItem={(child) => (
              <MenuNode
                key={child.id}
                node={child}
                depth={depth + 1}
                expanded={expanded}
                setExpanded={setExpanded}
                editingId={editingId}
                setEditingId={setEditingId}
                addingToParent={addingToParent}
                setAddingToParent={setAddingToParent}
                indent={indent}
                outdent={outdent}
                toggleVisibility={toggleVisibility}
                updateLabels={updateLabels}
                handleDelete={handleDelete}
                handleAdd={handleAdd}
                handleDragEnd={handleDragEnd}
                sensors={sensors}
              />
            )}
          />
          {addingToParent === node.id && (
            <NewMenuRow onCancel={() => setAddingToParent(null)} onSubmit={(payload) => handleAdd(node.id, payload)} />
          )}
        </div>
      )}
    </>
  );
}

function NewMenuRow({ onCancel, onSubmit }) {
  const [labelEs, setLabelEs] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [path, setPath] = useState('');
  const [isDropdown, setIsDropdown] = useState(false);
  const [pathKind, setPathKind] = useState('route');
  const [icon, setIcon] = useState('');

  const ICON_CHOICES = ['', 'facebook', 'instagram', 'linkedin', 'twitter', 'youtube',
                        'globe', 'mail', 'phone', 'external-link', 'rss', 'github',
                        'map-pin', 'calendar'];

  function submit() {
    if (!labelEs.trim()) return;
    onSubmit({
      label_es: labelEs.trim(),
      label_en: (labelEn.trim() || labelEs.trim()),
      path: path.trim(),
      path_kind: isDropdown ? 'route' : pathKind,
      icon: icon || '',
      is_dropdown: isDropdown,
    });
    setLabelEs(''); setLabelEn(''); setPath(''); setIsDropdown(false); setPathKind('route'); setIcon('');
  }

  return (
    <div data-testid="menu-new-row" className="space-y-2 p-3 rounded-lg border border-dashed border-cmic-300 bg-cmic-50/40 mt-2">
      <div className="flex flex-wrap items-center gap-2">
        <input value={labelEs} onChange={(e) => setLabelEs(e.target.value)} placeholder="Etiqueta ES" data-testid="menu-new-label-es" className="flex-1 min-w-[140px] rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
        <input value={labelEn} onChange={(e) => setLabelEn(e.target.value)} placeholder="Label EN" data-testid="menu-new-label-en" className="flex-1 min-w-[140px] rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
        <label className="text-xs text-charcoal-600 inline-flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={isDropdown} onChange={(e) => setIsDropdown(e.target.checked)} data-testid="menu-new-dropdown" />
          Sólo contenedor
        </label>
      </div>
      {!isDropdown && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={pathKind}
            onChange={(e) => setPathKind(e.target.value)}
            data-testid="menu-new-path-kind"
            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm"
          >
            <option value="route">Ruta interna</option>
            <option value="bilingual">Slug bilingüe (ES|EN)</option>
            <option value="external_url">URL externa</option>
          </select>
          <input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder={
              pathKind === 'external_url' ? 'https://facebook.com/cmic.mx'
              : pathKind === 'bilingual'  ? 'es-slug|en-slug'
              : 'ruta-interna'
            }
            data-testid="menu-new-path"
            className="flex-1 min-w-[200px] rounded-md border border-gray-200 px-2 py-1.5 text-sm"
          />
          <select
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            data-testid="menu-new-icon"
            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm"
            title="Icono opcional"
          >
            {ICON_CHOICES.map((ic) => (
              <option key={ic || 'none'} value={ic}>{ic || '— sin icono —'}</option>
            ))}
          </select>
        </div>
      )}
      <div className="flex items-center gap-2">
        <button onClick={submit} data-testid="menu-new-submit" className="inline-flex items-center gap-1 bg-cmic-600 text-white rounded-md px-3 py-1.5 text-sm font-medium hover:bg-cmic-700">
          <Plus size={13} />Añadir
        </button>
        <button onClick={onCancel} data-testid="menu-new-cancel" className="p-1.5 text-charcoal-500 hover:text-rose-600 hover:bg-rose-50 rounded-md">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
